import { ResponsiveBar } from '@nivo/bar';
import styled, { css } from 'styled-components';
import {
  flexBoxCenter,
  justifyContentFlexEnd,
  scrollbarX,
  layerA90Deg,
  layerADark,
  layerBDark,
} from '../../styles/commonStyles';
import { useSelector } from 'react-redux';
import { selectTelemetry } from '../../store/slices/telemetrySlice';
import { useState, useCallback, useEffect, useRef } from 'react';

import { selectMC } from '../../store/slices/mCSlice';
import moment from 'moment';
import {
  dataConsumptionSeriesUnit,
  formatChartBarLabel,
  formatDateTooltip,
  formatTelemetryChartData,
  getSelectedDeviceIds,
  getStartAndEndTimestamps,
  toDataConsumption,
} from '../../../helpers/helpers';
import { selectUnits } from '../../store/slices/settings/unitsSlice';
import { getTelemetryService } from '../../../services';
import { selectEssSwitch } from '../../store/slices/essSwitchSlice';
import { selectTesSwitch } from '../../store/slices/tesSwitchSlice';
import { selectTgsSwitch } from '../../store/slices/tgsSwitchSlice';
import { selectMasterControls } from '../../store/slices/masterControlSelectSlice';
import {
  applyLatestHpDemo,
  hpDemoRows,
  loadHpDemoTelemetry,
  startLatestRequest,
} from '../demo/loadHpDemoTelemetry';
import { highlightClickedBar } from './highlightClickedBar';

const NivoChartCopy = ({
  telemetryData,
  telemetryDataKeys,
  setTelemetryData,
  setTelemetryDataKeys,
  copyTelemetryState,
  copyTelemetryStateKeys,
  copyTelemetryDailyState,
  setCopyTelemetryState,
  setCopyTelemetryDailyState,
  doubleTelemetryData,
  setDoubleTelemetryData,
  doubleTelemetryKeys,
  setDoubleTelemetryKeys,
}) => {
  const styles = {
    fontFamily: 'orbitron',
    fontWeight: '800',
  };

  const telemetryState = useSelector(selectTelemetry);
  const { isSearch, selectChartSystem } = telemetryState;

  const selectionOfSystem = useSelector(selectMC);
  const { essDc, tgsTesDc, tesDc, hpDc, tgs, hpGc, ess, tes, hpEc } =
    selectionOfSystem.selectSystem;

  const intervalUnit = useSelector(
    (state) => state.telemetryChartData.intervalUnit,
  );

  const { essSwitch } = useSelector(selectEssSwitch);
  const { tesSwitch } = useSelector(selectTesSwitch);
  const { tgsSwitch } = useSelector(selectTgsSwitch);
  const { isF } = useSelector(selectUnits);
  const masterControlSelects = useSelector(selectMasterControls);

  const [zoom, setZoom] = useState('pointer');
  const [checkChangeOfData, setCheckChangeOfData] = useState(false);

  const CHART_SCALE_MIN = 1;
  const CHART_SCALE_MAX = 4;
  const CHART_SCALE_STEP = 0.5;
  const [chartScale, setChartScale] = useState(CHART_SCALE_MIN);

  const handleScaleIn = () =>
    setChartScale((scale) =>
      Math.min(CHART_SCALE_MAX, scale + CHART_SCALE_STEP),
    );
  const handleScaleOut = () =>
    setChartScale((scale) =>
      Math.max(CHART_SCALE_MIN, scale - CHART_SCALE_STEP),
    );

  const tooltipSystemAbr =
    essDc || tgsTesDc || hpDc
      ? 'dc'
      : ess || tes || hpEc
        ? 'ec'
        : (tgs || hpGc) && 'gc';

  const tooltipMeasureUnits =
    ess || tes || hpEc ? 'kw' : (tgs || hpGc) && isF ? 'FT³' : 'M³';

  const colorOfBottomAxisDot = ess
    ? '#83FFFF'
    : tgs
      ? '#FF7800'
      : tes
        ? '#95FF45'
        : essDc || tgsTesDc
          ? '#FF0080'
          : (hpDc || hpEc || hpGc) && '';

  const isDataConsumption = Boolean(essDc || tgsTesDc || tesDc || hpDc);

  const titleOfLeftAxis = isDataConsumption
    ? 'data consumption'
    : ess || tes || hpEc
      ? 'energy consumption (kw)'
      : `gas consumption (${isF ? 'FT³' : 'M³'})`;

  let SVG_WIDTH = null;
  if (isSearch) {
    if (selectChartSystem) {
      SVG_WIDTH =
        100 * doubleTelemetryData.first.length > 1184
          ? 100 * doubleTelemetryData.first.length
          : 1184;
    } else {
      SVG_WIDTH =
        100 * telemetryData.length > 1184 ? 100 * telemetryData.length : 1184;
    }
    SVG_WIDTH = Math.round(SVG_WIDTH * chartScale);
  }

  // Bumped by every HP zoom click, zoom out and new search, so a slow HP demo
  // load only lands if nothing newer happened while it was loading.
  const zoomRequest = useRef(0);
  useEffect(() => {
    zoomRequest.current += 1;
  }, [copyTelemetryState]);

  const handleZoomOut = () => {
    zoomRequest.current += 1;
    if (checkChangeOfData) {
      setTelemetryData(copyTelemetryState);
      setTelemetryDataKeys(copyTelemetryStateKeys);
      setCheckChangeOfData(false);
    }
    setZoom('pointer');
  };

  const handleClickBar = useCallback(
    (id, selectedData) => {
      const rows = checkChangeOfData
        ? copyTelemetryDailyState
        : copyTelemetryState;
      const highlighted = highlightClickedBar(rows ?? [], selectedData, id);
      if (highlighted) {
        setTelemetryData(highlighted);
      }

      const handleZoomIn = (swtSwitch, swtName, category) => {
        if (!checkChangeOfData) {
          const deviceIds = getSelectedDeviceIds(
            masterControlSelects,
            swtSwitch,
            swtName,
          );
          const { start: startDate, end: endDate } = getStartAndEndTimestamps(
            id,
            intervalUnit,
          );

          getTelemetryService({ deviceIds, startDate, endDate, category }).then(
            ({ data }) => {
              const { intervalData, intervalKeys } = formatTelemetryChartData(
                data,
                swtName,
                swtSwitch,
                isF,
              );

              setTelemetryData(intervalData);
              setTelemetryDataKeys(intervalKeys);
              setCopyTelemetryDailyState(intervalData);
              setCheckChangeOfData(true);
            },
          );
        }
      };

      const handleZoomOut = () => {
        setTelemetryData(copyTelemetryState);
        setTelemetryDataKeys(copyTelemetryStateKeys);
        setCheckChangeOfData(false);
      };

      if (zoom === 'zoomIn') {
        if (ess) {
          handleZoomIn(essSwitch, 'ess', 'energy');
        } else if (tgs) {
          handleZoomIn(tgsSwitch, 'tgs', 'fuel');
        } else if (tes) {
          handleZoomIn(tesSwitch, 'tes', 'energy');
        } else if (hpGc || hpEc) {
          const system = hpGc ? 'hpGc' : 'hpEc';
          const isLatest = startLatestRequest(zoomRequest);
          return applyLatestHpDemo(loadHpDemoTelemetry(), isLatest, (data) => {
            const { dailyRows, dailyKeys } = hpDemoRows(system, data);
            setTelemetryData(dailyRows);
            setTelemetryDataKeys(dailyKeys);
            setCopyTelemetryDailyState(dailyRows);
            setCheckChangeOfData(true);
          });
        }
      } else if (zoom === 'zoomOut') {
        if (ess) {
          handleZoomOut();
        } else if (tgs) {
          handleZoomOut();
        } else if (tes) {
          handleZoomOut();
        } else if (hpGc || hpEc) {
          const system = hpGc ? 'hpGc' : 'hpEc';
          const isLatest = startLatestRequest(zoomRequest);
          return applyLatestHpDemo(loadHpDemoTelemetry(), isLatest, (data) => {
            const { rows, keys } = hpDemoRows(system, data);
            setTelemetryData(rows);
            setTelemetryDataKeys(keys);
            setCheckChangeOfData(false);
          });
        }
      }
    },
    [
      zoom,
      checkChangeOfData,
      copyTelemetryState,
      copyTelemetryStateKeys,
      copyTelemetryDailyState,
      setTelemetryData,
      setTelemetryDataKeys,
      setCopyTelemetryDailyState,
      ess,
      tgs,
      tes,
      hpGc,
      hpEc,
      essSwitch,
      tgsSwitch,
      tesSwitch,
      intervalUnit,
      isF,
      masterControlSelects,
    ],
  );

  const handleClickFirstBar = useCallback(
    (id, selectedData) => {
      const copyFirstYearMonthlyData =
        copyTelemetryState.firstYearMonthlyData?.map((data) => ({ ...data }));
      const copyFirstYearDailyData = copyTelemetryState.firstYearDailyData?.map(
        (data) => ({ ...data }),
      );
      const copySecondYearMonthlyData =
        copyTelemetryState.secondYearMonthlyData?.map((data) => ({ ...data }));
      const copySecondYearDailyData =
        copyTelemetryState.secondYearDailyData?.map((data) => ({ ...data }));

      if (checkChangeOfData) {
        const newCopyData = {
          first: copyFirstYearDailyData,
          second: copySecondYearDailyData,
        };
        newCopyData.first.filter((value) => {
          if (value.switch === selectedData.switch) {
            return setDoubleTelemetryData(
              newCopyData,
              (value[`${id}Color`] = 'red'),
            );
          }
        });
      } else {
        const newCopyData = {
          first: copyFirstYearMonthlyData,
          second: copySecondYearMonthlyData,
        };
        newCopyData.first.filter((value) => {
          if (value.switch === selectedData.switch) {
            return setDoubleTelemetryData(
              newCopyData,
              (value[`${id}Color`] = 'red'),
            );
          }
        });
      }

      if (zoom === 'zoomIn') {
        setDoubleTelemetryData({
          first: copyTelemetryState.firstYearDailyData,
          second: copyTelemetryState.secondYearDailyData,
        });
        setDoubleTelemetryKeys({
          first: copyTelemetryState.firstYearDailyKeys,
          second: copyTelemetryState.secondYearDailyKeys,
        });
        setCheckChangeOfData(true);
      } else if (zoom === 'zoomOut') {
        setDoubleTelemetryKeys({
          first: copyTelemetryState.firstYearMonthlyKeys,
          second: copyTelemetryState.secondYearMonthlyKeys,
        });
        setDoubleTelemetryData({
          first: copyTelemetryState.firstYearMonthlyData,
          second: copyTelemetryState.secondYearMonthlyData,
        });
        setCheckChangeOfData(false);
      }
    },
    [doubleTelemetryData, zoom],
  );

  const handleClickSecondBar = useCallback(
    (id, selectedData) => {
      const copyFirstYearMonthlyData =
        copyTelemetryState.firstYearMonthlyData?.map((data) => ({ ...data }));
      const copyFirstYearDailyData = copyTelemetryState.firstYearDailyData?.map(
        (data) => ({ ...data }),
      );
      const copySecondYearMonthlyData =
        copyTelemetryState.secondYearMonthlyData?.map((data) => ({ ...data }));
      const copySecondYearDailyData =
        copyTelemetryState.secondYearDailyData?.map((data) => ({ ...data }));

      if (checkChangeOfData) {
        const newCopyData = {
          first: copyFirstYearDailyData,
          second: copySecondYearDailyData,
        };
        newCopyData.second.filter((value) => {
          if (value.switch === selectedData.switch) {
            return setDoubleTelemetryData(
              newCopyData,
              (value[`${id}Color`] = 'red'),
            );
          }
        });
      } else {
        const newCopyData = {
          second: copySecondYearMonthlyData,
          first: copyFirstYearMonthlyData,
        };

        newCopyData.second.filter((value) => {
          if (value.switch === selectedData.switch) {
            return setDoubleTelemetryData(
              newCopyData,
              (value[`${id}Color`] = 'red'),
            );
          }
        });
      }

      if (zoom === 'zoomIn') {
        setDoubleTelemetryData({
          first: copyTelemetryState.firstYearDailyData,
          second: copyTelemetryState.secondYearDailyData,
        });
        setDoubleTelemetryKeys({
          first: copyTelemetryState.firstYearDailyKeys,
          second: copyTelemetryState.secondYearDailyKeys,
        });
        setCheckChangeOfData(true);
      } else if (zoom === 'zoomOut') {
        setDoubleTelemetryKeys({
          first: copyTelemetryState.firstYearMonthlyKeys,
          second: copyTelemetryState.secondYearMonthlyKeys,
        });
        setDoubleTelemetryData({
          first: copyTelemetryState.firstYearMonthlyData,
          second: copyTelemetryState.secondYearMonthlyData,
        });
        setCheckChangeOfData(false);
      }
    },
    [doubleTelemetryData, zoom],
  );

  const lineAndBarXPosition =
    telemetryData.length === 10
      ? { first: 20, second: 200, third: 75, fourth: 55, fifth: 205 }
      : telemetryData.length === 9
        ? { first: 22, second: 198, third: 83, fourth: 60, fifth: 210 }
        : telemetryData.length === 8
          ? { first: 24, second: 196, third: 91, fourth: 65, fifth: 215 }
          : telemetryData.length === 7
            ? { first: 22, second: 198, third: 105, fourth: 75, fifth: 225 }
            : telemetryData.length === 6
              ? { first: 22, second: 198, third: 125, fourth: 90, fifth: 240 }
              : telemetryData.length === 5
                ? {
                    first: 22,
                    second: 198,
                    third: 150,
                    fourth: 100,
                    fifth: 250,
                  }
                : telemetryData.length === 4
                  ? {
                      first: 22,
                      second: 198,
                      third: 180,
                      fourth: 120,
                      fifth: 270,
                    }
                  : telemetryData.length === 3
                    ? {
                        first: 22,
                        second: 198,
                        third: 230,
                        fourth: 150,
                        fifth: 300,
                      }
                    : telemetryData.length === 2
                      ? {
                          first: 22,
                          second: 198,
                          third: 300,
                          fourth: 205,
                          fifth: 355,
                        }
                      : telemetryData.length === 1
                        ? {
                            first: 22,
                            second: 198,
                            third: 700,
                            fourth: 330,
                            fifth: 480,
                          }
                        : {
                            first: 20,
                            second: 200,
                            third: 65,
                            fourth: 50,
                            fifth: 200,
                          };

  const CustomYLines = ({ bars }) => {
    return bars.map((bar, idx) => {
      const rotationTEC = `rotate(-90 ${bar.x - 10} 202)`;
      const rotationTHU = `rotate(-90 ${bar.x + 45} 187.5)`;

      return (
        <div key={idx}>
          {!bar.data.data.totalEnergyConsumption ? (
            ''
          ) : (
            <>
              <rect
                width='10'
                height='100'
                x={bar.x - lineAndBarXPosition.first}
                y={108}
                stroke='#1B2B44'
                strokeWidth='2'
                fill='#233a54'
              ></rect>

              <text
                x={bar.x - 14}
                y={lineAndBarXPosition.second}
                fill={bar.data.data.barColor}
                fontSize={8}
                transform={rotationTEC}
                z-index={2}
              >
                {bar.data.data.totalEnergyConsumption}
              </text>
            </>
          )}

          <line
            key={bar.key}
            x1={bar.x + lineAndBarXPosition.third}
            y1={210}
            x2={bar.x + lineAndBarXPosition.third}
            y2={0}
            stroke='#fff'
            strokeWidth={1.5}
            style={{ pointerEvents: 'none' }}
          />
          {!bar.data.data.totalUsageHours ? (
            ''
          ) : (
            <>
              <rect
                width='10'
                height={100}
                x={bar.x + lineAndBarXPosition.fourth}
                y={108}
                stroke='#1B2B44'
                strokeWidth='2'
                fill='#233a54'
              ></rect>
              <text
                x={bar.x + 26}
                y={lineAndBarXPosition.fifth}
                fill={bar.data.data.barColor}
                fontSize={8}
                transform={rotationTHU}
                z-index={2}
              >
                {bar.data.data.totalUsageHours}
              </text>
            </>
          )}
        </div>
      );
    });
  };

  const CustomYLinesTopChart = ({ bars }) => {
    return bars.map((bar, index) => {
      const rotationTEC = `rotate(-90 ${bar.x - 10} 202)`;
      const rotationTHU = `rotate(-90 ${bar.x + 45} 187.5)`;

      return (
        <g key={index}>
          {!bar.data.data.totalEnergyConsumption ? (
            ''
          ) : (
            <>
              <rect
                width='10'
                height='100'
                x={bar.x - 14}
                y={128}
                stroke='#1B2B44'
                strokeWidth='2'
                fill='#233a54'
              ></rect>

              <text
                x={bar.x - 34}
                y={206}
                fill={bar.color}
                fontSize={8}
                transform={rotationTEC}
                z-index={2}
              >
                {bar.data.data.totalEnergyConsumption}
              </text>
            </>
          )}

          <line
            key={bar.key}
            x1={bar.x + 65}
            y1={230}
            x2={bar.x + 65}
            y2={0}
            stroke='#fff'
            strokeWidth={1.5}
            style={{ pointerEvents: 'none' }}
          />
          {!bar.data.data.totalUsageHours ? (
            ''
          ) : (
            <>
              <rect
                width='10'
                height={100}
                x={bar.x + 44}
                y={130}
                stroke='#1B2B44'
                strokeWidth='2'
                fill='#233a54'
              ></rect>
              <text
                x={bar.x + 6}
                y={194}
                fill={bar.color}
                fontSize={8}
                transform={rotationTHU}
                z-index={2}
              >
                {bar.data.data.totalUsageHours}
              </text>
            </>
          )}
        </g>
      );
    });
  };

  const CustomYLinesBottomChart = ({ bars }) => {
    return bars.map((bar, index) => {
      const rotationTEC = `rotate(-90 ${bar.x - 10} 202)`;
      const rotationTHU = `rotate(-90 ${bar.x + 45} 187.5)`;

      return (
        <g key={index}>
          {!bar.data.data.totalEnergyConsumption ? (
            ''
          ) : (
            <>
              <rect
                width='10'
                height='100'
                x={bar.x - 15}
                y={0}
                stroke='#1B2B44'
                strokeWidth='2'
                fill='#233a54'
              ></rect>

              <text
                x={bar.x + 94}
                y={205}
                fill={bar.color}
                fontSize={8}
                transform={rotationTEC}
                z-index={2}
              >
                {bar.data.data.totalEnergyConsumption}
              </text>
            </>
          )}

          <line
            key={bar.key}
            x1={bar.x + 65}
            y1={231}
            x2={bar.x + 65}
            y2={0}
            stroke='#fff'
            strokeWidth={1.5}
            style={{ pointerEvents: 'none' }}
          />
          {!bar.data.data.totalUsageHours ? (
            ''
          ) : (
            <>
              <rect
                width='10'
                height={100}
                x={bar.x + 44}
                y={0}
                stroke='#1B2B44'
                strokeWidth='2'
                fill='#233a54'
              ></rect>
              <text
                x={bar.x + 135}
                y={194}
                fill={bar.color}
                fontSize={8}
                transform={rotationTHU}
                z-index={2}
              >
                {bar.data.data.totalUsageHours}
              </text>
            </>
          )}
        </g>
      );
    });
  };

  const CustomLabels = ({ bars }) => {

    return (
      <>
        <text x={0} y={-10} fill={bars[0]?.color} fontSize={12} z-index={2}>
          {bars[0]?.data.data.month}
        </text>
      </>
    );
  };

  const CustomLabelsBottomChart = ({ bars }) => {

    return (
      <>
        <text x={0} y={250} fill={bars[0]?.color} font-size={12} z-index={2}>
          {bars[0]?.data.data.month}
        </text>
      </>
    );
  };

  const getTspanGroups = (value) => {
    return (
      <>
        <tspan>{value}</tspan>
      </>
    );
  };
  const toNumber = (value) =>
    Number(String(value).replace(/[^\d.-]/g, ''));

  const seriesValues = (rows = [], keys = []) =>
    (rows ?? []).flatMap((row) => (keys ?? []).map((key) => toNumber(row[key])));

  const dataConsumptionAxisUnit = dataConsumptionSeriesUnit(
    selectChartSystem
      ? [
          ...seriesValues(doubleTelemetryData.first, doubleTelemetryKeys.first),
          ...seriesValues(
            doubleTelemetryData.second,
            doubleTelemetryKeys.second,
          ),
        ]
      : seriesValues(telemetryData, telemetryDataKeys),
  );

  const toPlottedRows = (rows = [], keys = []) =>
    (rows ?? []).map((row) => {
      const plotted = { ...row };

      (keys ?? []).forEach((key) => {
        if (plotted[key]) {
          const numeric = toNumber(plotted[key]);
          plotted[key] = isDataConsumption
            ? toDataConsumption(numeric, dataConsumptionAxisUnit)
            : numeric;
        }
      });

      return plotted;
    });

  telemetryData = toPlottedRows(telemetryData, telemetryDataKeys);

  const plottedDoubleFirst = toPlottedRows(
    doubleTelemetryData.first,
    doubleTelemetryKeys.first,
  );
  const plottedDoubleSecond = toPlottedRows(
    doubleTelemetryData.second,
    doubleTelemetryKeys.second,
  );

  const axisLeftConfig = {
    tickSize: 5,
    tickPadding: 5,
    tickRotation: 0,
    legend: isDataConsumption
      ? `${titleOfLeftAxis} (${dataConsumptionAxisUnit})`
      : `${titleOfLeftAxis}`,
    legendPosition: 'middle',
    legendOffset: -68,
  };
  return (
    <>
      {isSearch ? (
        <ShadowWrapper1>
          <Wrapper>
            <ScrollbarContainer containerHeight={selectChartSystem}>
              <BarWrapper
                className='telemetry-report'
                width={SVG_WIDTH}
                style={styles}
                zoom={zoom}
                containerHeight={selectChartSystem}
                onClick={() => setZoom('pointer')}
              >
                {selectChartSystem ? (
                  <>
                    <ResponsiveBar
                      height={310}
                      data={plottedDoubleFirst}
                      keys={doubleTelemetryKeys.first}
                      indexBy={(_index) => _index.switch}
                      margin={{ top: 30, right: 30, bottom: 50, left: 80 }}
                      padding={0.55}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: false }}
                      colors={({ id, data }) => data[`${id}Color`]}
                      valueFormat=' >-10'
                      onClick={({ id, data }) => handleClickFirstBar(id, data)}
                      label={({ id }) =>
                        checkChangeOfData
                          ? ''
                          : formatChartBarLabel(id, intervalUnit)
                      }
                      labelSkipWidth={8}
                      labelSkipHeight={8}
                      labelTextColor='#1b2b44'
                      labelFontFamily='Orbitron'
                      labelFontWeight={800}
                      enableGridX={false}
                      gridXValues={0.5}
                      borderWidth={1}
                      borderColor={{
                        from: 'color',
                        modifiers: [['darker', 1.6]],
                      }}
                      axisTop={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legendPosition: 'middle',
                        legendOffset: 32,
                        renderTick: ({ x, y }) => {
                          return <g transform={`translate(${x},${y})`}></g>;
                        },
                      }}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'switch',
                        legendPosition: 'middle',
                        legendOffset: 46,
                        renderTick: ({
                          textAnchor,
                          textBaseline,
                          textX,
                          textY,
                          value,
                          x,
                          y,
                        }) => {
                          const switchName = value.split(' - ');
                          return (
                            <g transform={`translate(${x},${y})`}>
                              <circle
                                alignmentBaseline={textBaseline}
                                textAnchor={textAnchor}
                                transform={`translate(${
                                  textX - switchName[0].length * 3 - 8
                                },${textY + 26})`}
                                r={4}
                                fill={`${colorOfBottomAxisDot}`}
                              ></circle>

                              <text
                                alignmentBaseline={textBaseline}
                                textAnchor={textAnchor}
                                transform={`translate(${textX - 10},${
                                  textY + 28
                                })`}
                                fontSize={8}
                                fill={'#fff'}
                              >
                                {getTspanGroups(switchName[0])}
                              </text>
                              <text
                                alignmentBaseline={textBaseline}
                                textAnchor={textAnchor}
                                transform={`translate(${textX},${textY + 35})`}
                                fontSize={8}
                                fill={'#fff'}
                              >
                                {getTspanGroups(switchName[1])}
                              </text>
                            </g>
                          );
                        },
                      }}
                      axisLeft={axisLeftConfig}
                      theme={{
                        fontFamily: 'orbitron',
                        fontWeight: 800,
                        axis: {
                          legend: {
                            text: {
                              fill: '#ffff',
                              fontFamily: 'orbitron',
                              fontWeight: 800,
                            },
                          },

                          tickColor: '#eee',
                          ticks: {
                            line: {
                              stroke: '#ffff',
                              strokeWidth: 1,
                            },
                            text: {
                              fill: '#ffff',
                              fontFamily: 'orbitron',
                              fontWeight: 800,
                            },
                          },
                          domain: {
                            line: {
                              stroke: '#777777',
                              strokeWidth: 2,
                              strokeDasharray: 'solid',
                            },
                          },
                        },

                        grid: {
                          line: {
                            stroke: '#ffff',
                            strokeWidth: 1,
                          },
                        },
                      }}
                      tooltip={({ id, value, data }) => (
                        <div
                          style={{
                            width: '144px',
                            height: 'auto',
                            position: 'absolute',
                            top: '-15px',
                            left: '15px',
                            padding: 2,
                            border: '2px solid #1B2B44',
                            background: '#233A54',
                            fontSize: 8,
                          }}
                        >
                          <span>{data.switch}</span>
                          <br />
                          {moment(id).format(formatDateTooltip(intervalUnit))}
                          <br />
                          <span>
                            ({`${tooltipSystemAbr}`}: {value}{' '}
                            {`${tooltipMeasureUnits}`})
                          </span>
                          <br />
                          <span>hours of usage: {data[`${id}THU`]}</span>
                          <br />
                        </div>
                      )}
                      layers={[
                        'grid',
                        'markers',
                        'areas',
                        CustomYLinesTopChart,
                        checkChangeOfData && CustomLabels,
                        'lines',
                        'slices',
                        'axes',
                        'bars',
                        'points',
                        'legends',
                      ]}
                    />
                    <ResponsiveBar
                      height={310}
                      data={plottedDoubleSecond}
                      keys={doubleTelemetryKeys.second}
                      indexBy={(_index) => _index.switch}
                      margin={{ top: 30, right: 30, bottom: 50, left: 80 }}
                      padding={0.55}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={({ id, data }) => data[`${id}Color`]}
                      valueFormat=' >-10'
                      reverse={true}
                      onClick={({ id, data }) => handleClickSecondBar(id, data)}
                      label={({ id }) =>
                        checkChangeOfData
                          ? ''
                          : formatChartBarLabel(id, intervalUnit)
                      }
                      labelSkipWidth={8}
                      labelSkipHeight={8}
                      labelTextColor='#1b2b44'
                      labelFontFamily='Orbitron'
                      labelFontWeight={800}
                      enableGridX={false}
                      gridXValues={0.5}
                      borderWidth={1}
                      borderColor={{
                        from: 'color',
                        modifiers: [['darker', 1.6]],
                      }}
                      axisTop={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legendPosition: 'middle',
                        legendOffset: 32,
                        renderTick: ({ x, y }) => {
                          return <g transform={`translate(${x},${y})`}></g>;
                        },
                      }}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legendPosition: 'middle',
                        legendOffset: 32,
                        renderTick: ({ x, y }) => {
                          return <g transform={`translate(${x},${y})`}></g>;
                        },
                      }}
                      axisLeft={axisLeftConfig}
                      theme={{
                        fontFamily: 'orbitron',
                        fontWeight: 800,
                        axis: {
                          legend: {
                            text: {
                              fill: '#ffff',
                              fontFamily: 'orbitron',
                              fontWeight: 800,
                            },
                          },

                          tickColor: '#eee',
                          ticks: {
                            line: {
                              stroke: '#ffff',
                              strokeWidth: 1,
                            },
                            text: {
                              fill: '#ffff',
                              fontFamily: 'orbitron',
                              fontWeight: 800,
                            },
                          },
                          domain: {
                            line: {
                              stroke: '#777777',
                              strokeWidth: 2,
                              strokeDasharray: 'solid',
                            },
                          },
                        },

                        grid: {
                          line: {
                            stroke: '#ffff',
                            strokeWidth: 1,
                          },
                        },
                      }}
                      tooltip={({ id, value, data }) => (
                        <div
                          style={{
                            width: '144px',
                            height: 'auto',
                            position: 'absolute',
                            top: '-15px',
                            left: '15px',
                            padding: 2,
                            border: '2px solid #1B2B44',
                            background: '#233A54',
                            fontSize: 8,
                          }}
                        >
                          <span>{data.switch}</span>
                          <br />
                          {moment(id).format(formatDateTooltip(intervalUnit))}
                          <br />
                          <span>
                            ({`${tooltipSystemAbr}`}: {value}{' '}
                            {`${tooltipMeasureUnits}`})
                          </span>
                          <br />
                          <span>hours of usage: {data[`${id}THU`]}</span>
                          <br />
                        </div>
                      )}
                      layers={[
                        'grid',
                        'markers',
                        'areas',
                        CustomYLinesBottomChart,
                        checkChangeOfData && CustomLabelsBottomChart,
                        'lines',
                        'slices',
                        'axes',
                        'bars',
                        'points',
                        'legends',
                      ]}
                    />
                  </>
                ) : (
                  <>
                    <ResponsiveBar
                      data={telemetryData}
                      keys={telemetryDataKeys}
                      indexBy={(_index) => _index.switch}
                      margin={{ top: 30, right: 30, bottom: 50, left: 80 }}
                      padding={0.55}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={({ id, data }) => data[`${id}Color`]}
                      valueFormat=' >-10'
                      onClick={({ id, data }) => handleClickBar(id, data)}
                      label={({ id }) =>
                        checkChangeOfData
                          ? ''
                          : formatChartBarLabel(id, intervalUnit)
                      }
                      labelSkipWidth={8}
                      labelSkipHeight={8}
                      labelTextColor='#1b2b44'
                      labelFontFamily='Orbitron'
                      labelFontWeight={800}
                      enableGridX={false}
                      gridXValues={0.5}
                      borderWidth={1}
                      borderColor={{
                        from: 'color',
                        modifiers: [['darker', 1.6]],
                      }}
                      axisTop={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legendPosition: 'middle',
                        legendOffset: 32,
                        renderTick: ({ x, y }) => {
                          return <g transform={`translate(${x},${y})`}></g>;
                        },
                      }}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'switch',
                        legendPosition: 'middle',
                        legendOffset: 42,
                        renderTick: ({
                          textAnchor,
                          textBaseline,
                          textX,
                          textY,
                          value,
                          x,
                          y,
                          data,
                        }) => {
                          const switchName = value.split(' - ');
                          return (
                            <g transform={`translate(${x},${y})`}>
                              <circle
                                alignmentBaseline={textBaseline}
                                textAnchor={textAnchor}
                                transform={`translate(${
                                  textX - switchName[0].length * 3 - 8
                                },${textY - 1})`}
                                r={4}
                                fill={`${colorOfBottomAxisDot}`}
                              ></circle>

                              <text
                                alignmentBaseline={textBaseline}
                                textAnchor={textAnchor}
                                transform={`translate(${textX},${textY + 3})`}
                                fontSize={8}
                                fill={'#fff'}
                              >
                                {getTspanGroups(switchName[0])}
                              </text>
                              <text
                                alignmentBaseline={textBaseline}
                                textAnchor={textAnchor}
                                transform={`translate(${textX},${textY + 15})`}
                                fontSize={8}
                                fill={'#fff'}
                              >
                                {getTspanGroups(switchName[1])}
                              </text>
                            </g>
                          );
                        },
                      }}
                      axisLeft={axisLeftConfig}
                      theme={{
                        fontFamily: 'orbitron',
                        fontWeight: 800,
                        axis: {
                          legend: {
                            text: {
                              fill: '#ffff',
                              fontFamily: 'orbitron',
                              fontWeight: 800,
                            },
                          },

                          tickColor: '#eee',
                          ticks: {
                            line: {
                              stroke: '#ffff',
                              strokeWidth: 1,
                            },
                            text: {
                              fill: '#ffff',
                              fontFamily: 'orbitron',
                              fontWeight: 800,
                            },
                          },
                          domain: {
                            line: {
                              stroke: '#777777',
                              strokeWidth: 2,
                              strokeDasharray: 'solid',
                            },
                          },
                        },

                        grid: {
                          line: {
                            stroke: '#ffff',
                            strokeWidth: 1,
                          },
                        },
                      }}
                      tooltip={({ id, value, data }) => (
                        <div
                          style={{
                            width: '144px',
                            height: 'auto',
                            position: 'absolute',
                            top: '-15px',
                            left: '15px',
                            padding: 2,
                            border: '2px solid #1B2B44',
                            background: '#233A54',
                            fontSize: 8,
                          }}
                        >
                          <span>{data.switch}</span>
                          <br />
                          {moment(id).format(formatDateTooltip(intervalUnit))}

                          <br />

                          <span>
                            ({`${tooltipSystemAbr}`}:{' '}
                            {tooltipSystemAbr === 'dc'
                              ? Number(value).toFixed(2)
                              : value}{' '}
                            {`${
                              tooltipSystemAbr === 'dc'
                                ? dataConsumptionAxisUnit
                                : tooltipMeasureUnits
                            }`})
                          </span>
                          <br />
                          {!(essDc || tgsTesDc || tesDc) && (
                            <>
                              <span>hours of usage: {data[`${id}THU`]}</span>
                              <br />
                            </>
                          )}
                        </div>
                      )}
                      layers={[
                        'grid',
                        'markers',
                        'areas',
                        CustomYLines,
                        checkChangeOfData && CustomLabels,
                        'lines',
                        'slices',
                        'axes',
                        'bars',
                        'points',
                        'legends',
                      ]}
                    />
                  </>
                )}
              </BarWrapper>
            </ScrollbarContainer>

            <ZoomContainer>
              <Img
                src={'./images/zoomButtonMinus.svg'}
                onClick={handleScaleOut}
                $disabled={chartScale <= CHART_SCALE_MIN}
              />
              <Span>scale {chartScale}x</Span>
              <Img
                src={'./images/zoomButtonPlus.svg'}
                onClick={handleScaleIn}
                $disabled={chartScale >= CHART_SCALE_MAX}
              />
            </ZoomContainer>

            {essDc || tgsTesDc || tesDc || hpDc ? (
              ''
            ) : (
              <ZoomContainer>
                <Img
                  src={'./images/zoomButtonMinus.svg'}
                  onClick={handleZoomOut}
                />
                <Span>detail</Span>
                <Img
                  src={'./images/zoomButtonPlus.svg'}
                  onClick={() => setZoom('zoomIn')}
                  enableGreenBorder={zoom}
                />
              </ZoomContainer>
            )}
          </Wrapper>
        </ShadowWrapper1>
      ) : (
        <ShadowWrapper>
          <img src={'./images/noDataTelemetryChart.svg'} />
        </ShadowWrapper>
      )}
    </>
  );
};

export default NivoChartCopy;

const ShadowWrapper1 = styled.div`
  width: 1202px;
  height: auto;
  padding-top: 2px;
  padding-bottom: 2px;

  ${layerBDark}

  border-radius: 5px;
  opacity: 1;
  ${flexBoxCenter}
`;

const Wrapper = styled.div`
  width: 1198px;
  height: auto;
  padding-bottom: 4px;
  ${layerA90Deg}

  border-radius: 4px;
  opacity: 1;
  display: flex;
  flex-direction: column;
  ${flexBoxCenter}
`;

const ScrollbarContainer = styled.div`
  width: 1190px;

  height: ${({ containerHeight }) => (containerHeight ? '650px' : '310px')};
  margin-top: 4px;

  ${scrollbarX}

  opacity: 1;
`;

const BarWrapper = styled.div`
  width: ${({ width }) => width}px;
  height: 98%;

  ${({ zoom }) =>
    zoom === 'pointer'
      ? 'cursor:pointer '
      : zoom === 'zoomIn'
        ? 'cursor:zoom-in'
        : 'cursor:zoom-out'};

  ${layerADark}

  border-radius: 3px;
  ${flexBoxCenter};
  flex-direction: column;
`;

const ShadowWrapper = styled.div`
  width: 1202px;
  height: 150px;

  ${layerBDark}

  border-radius: 5px;
  opacity: 1;
  ${flexBoxCenter}
`;

const ZoomContainer = styled.div`
  width: 98%;
  height: 28px;
  margin-top: 8px;
  ${justifyContentFlexEnd}
`;

const Img = styled.img`
  width: fit-content;
  border-radius: 50%;
  cursor: pointer;
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
    `}
  ${({ enableGreenBorder }) =>
    enableGreenBorder === 'zoomOut'
      ? css`
           {
            &:first-child {
              border: 1px solid #95ff45;
            }
          }
        `
      : enableGreenBorder === 'zoomIn' &&
        css`
           {
            &:last-child {
              border: 1px solid #95ff45;
            }
          }
        `};
`;

const Span = styled.span`
  font-size: 10px;
  margin-left: 8px;
  margin-right: 8px;
`;
